import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut, RefreshCw, Check, X, ArrowLeft, Repeat } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { AvailabilityTab } from "../components/AdminAvailability";

const STATUS = {
  pending: { label: "In attesa", cls: "bg-amber-500/10 border-amber-500/40 text-amber-400" },
  confirmed: { label: "Confermato", cls: "bg-emerald-500/10 border-emerald-500/40 text-emerald-400" },
  declined: { label: "Rifiutato", cls: "bg-red-500/10 border-red-500/40 text-red-400" },
};

const Badge = ({ status }) => (
  <span data-testid="admin-request-status-badge"
    className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS[status]?.cls || STATUS.pending.cls}`}>
    {STATUS[status]?.label || status}
  </span>
);

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
      toast.success(`Benvenuto, ${data.name}!`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 grid-overlay">
      <div data-testid="admin-login-modal"
        className="w-full max-w-md bg-[#10141D]/95 backdrop-blur-xl border border-[#222A3B] rounded-3xl p-8 shadow-2xl">
        <img src="/logo.png" alt="Personal and Team Coach" className="h-14 w-14 rounded-xl object-cover ring-1 ring-white/10 mb-3" />
        <h1 className="text-2xl font-bold text-white font-heading">Area Riservata Coach</h1>
        <p className="text-sm text-slate-400 mt-1 mb-7">Accedi per gestire appuntamenti e richieste.</p>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <Label htmlFor="admin-email" className="text-slate-300">Email</Label>
            <Input id="admin-email" data-testid="admin-login-email-input" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="admin@mindcore.it"
              className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
          </div>
          <div>
            <Label htmlFor="admin-password" className="text-slate-300">Password</Label>
            <Input id="admin-password" data-testid="admin-login-password-input" type="password" required value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
              className="mt-2 bg-[#08090C] border-[#222A3B] text-white focus:border-sky-500" />
          </div>
          <Button type="submit" data-testid="admin-login-submit" disabled={loading}
            className="w-full bg-sky-400 text-[#050B14] hover:bg-sky-300 font-semibold h-11">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Accedi"}
          </Button>
        </form>
        <Link to="/" className="mt-6 flex items-center justify-center gap-1.5 text-sm text-slate-400 hover:text-sky-400 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Torna al sito
        </Link>
      </div>
    </div>
  );
};

const ItemCard = ({ item, type, onUpdate }) => {
  const [updating, setUpdating] = useState(false);
  const id = type === "booking" ? item.booking_id : item.request_id;

  const setStatus = async (status) => {
    setUpdating(true);
    try {
      const { data } = await api.patch(`/${type === "booking" ? "bookings" : "free-sessions"}/${id}`, { status });
      onUpdate(data);
      toast.success(status === "confirmed" ? "Confermato e notificato via email" : "Aggiornato");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div data-testid={`admin-item-${id}`} className="bg-[#10141D] border border-[#222A3B] rounded-2xl p-5 hover:border-sky-500/30 transition-colors">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-white">{item.name}</p>
          <p className="text-xs text-slate-500 mt-0.5">{item.email} · {item.phone}</p>
        </div>
        <div className="flex items-center gap-2">
          {item.series_id && (
            <span data-testid={`admin-series-badge-${item.booking_id}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-blue-500/10 border-blue-500/40 text-blue-400">
              <Repeat className="w-3 h-3" /> Serie
            </span>
          )}
          <Badge status={item.status} />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-300">
        <span className="text-sky-400 font-medium">{item.sport}</span>
        {type === "booking" ? (
          <span>{item.date} alle {item.time_slot}</span>
        ) : (
          <span>Età atleta: {item.athlete_age}</span>
        )}
      </div>
      {(item.notes || item.challenge) && (
        <p className="mt-2 text-sm text-slate-400 italic">"{item.notes || item.challenge}"</p>
      )}
      {item.status === "pending" && (
        <div className="mt-4 flex gap-3">
          <Button size="sm" data-testid="admin-appointment-confirm-btn" disabled={updating}
            onClick={() => setStatus("confirmed")}
            className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/25">
            <Check className="w-4 h-4 mr-1.5" /> Conferma
          </Button>
          <Button size="sm" data-testid="admin-appointment-decline-btn" disabled={updating}
            onClick={() => setStatus("declined")}
            className="bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20">
            <X className="w-4 h-4 mr-1.5" /> Rifiuta
          </Button>
        </div>
      )}
    </div>
  );
};

const Empty = ({ text }) => (
  <p className="text-sm text-slate-500 py-8 text-center border border-dashed border-[#222A3B] rounded-2xl">{text}</p>
);

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [bookings, setBookings] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [reports, setReports] = useState([]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const load = useCallback(async () => {
    try {
      const [b, s, m, r] = await Promise.all([
        api.get("/bookings"), api.get("/free-sessions"), api.get("/contact"), api.get("/safeguarding"),
      ]);
      setBookings(b.data); setSessions(s.data); setMessages(m.data); setReports(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  if (user === undefined) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-sky-400 animate-spin" /></div>;
  }
  if (!user) return <LoginForm onLogin={setUser} />;

  const updateIn = (setter) => (updated) =>
    setter((list) => list.map((i) => (i.booking_id || i.request_id) === (updated.booking_id || updated.request_id) ? updated : i));

  const seriesAction = async (seriesId, status) => {
    try {
      const { data } = await api.post(`/bookings/series/${seriesId}/status`, { status });
      toast.success(status === "confirmed"
        ? `Serie confermata (${data.updated} sessioni) e notificata via email`
        : `Serie cancellata (${data.updated} sessioni)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pendingCount = bookings.filter((b) => b.status === "pending").length + sessions.filter((s) => s.status === "pending").length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#08090C]">
      <header className="backdrop-blur-xl bg-[#08090C]/80 border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Personal and Team Coach" className="h-9 w-9 rounded-lg object-cover ring-1 ring-white/10" />
            <div>
              <p className="font-heading font-bold text-white leading-none">Dashboard Coach</p>
              <p className="text-xs text-slate-500 mt-0.5">{user.email}</p>
            </div>
            {pendingCount > 0 && (
              <span data-testid="admin-pending-count" className="ml-2 px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/15 border border-amber-500/40 text-amber-400">
                {pendingCount} in attesa
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" data-testid="admin-refresh-btn" onClick={load} className="text-slate-300 hover:text-sky-400">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-sky-400">
                <ArrowLeft className="w-4 h-4 mr-1" /> Sito
              </Button>
            </Link>
            <Button variant="outline" size="sm" data-testid="admin-logout-btn" onClick={logout}
              className="border-white/15 text-slate-300 hover:text-white bg-transparent">
              <LogOut className="w-4 h-4 mr-1.5" /> Esci
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <Tabs defaultValue="bookings">
          <TabsList className="bg-[#10141D] border border-[#222A3B]">
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Appuntamenti ({bookings.length})</TabsTrigger>
            <TabsTrigger value="sessions" data-testid="admin-tab-sessions">Sessioni Gratuite ({sessions.length})</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages">Messaggi ({messages.length})</TabsTrigger>
            <TabsTrigger value="safeguarding" data-testid="admin-tab-safeguarding">Segnalazioni ({reports.length})</TabsTrigger>
            <TabsTrigger value="availability" data-testid="admin-tab-availability">Disponibilità</TabsTrigger>
          </TabsList>
          <TabsContent value="bookings" className="mt-6 space-y-4">
            {(() => {
              const groups = {};
              bookings.forEach((b) => { if (b.series_id) (groups[b.series_id] = groups[b.series_id] || []).push(b); });
              const series = Object.entries(groups).filter(([, list]) => list.length > 1);
              if (!series.length) return null;
              return (
                <div className="space-y-3 mb-2">
                  {series.map(([sid, list]) => {
                    const pending = list.filter((x) => x.status === "pending").length;
                    return (
                      <div key={sid} data-testid={`admin-series-${sid}`}
                        className="bg-blue-500/5 border border-blue-500/30 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-white font-medium flex items-center gap-2">
                            <Repeat className="w-4 h-4 text-blue-400" /> Serie settimanale di {list[0].name}
                          </p>
                          <p className="text-xs text-slate-400 mt-1">
                            {list.length} sessioni · ogni settimana alle {list[0].time_slot} · {pending} in attesa
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {pending > 0 && (
                            <Button size="sm" data-testid="admin-series-confirm-btn" onClick={() => seriesAction(sid, "confirmed")}
                              className="bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/25">
                              <Check className="w-4 h-4 mr-1.5" /> Conferma serie
                            </Button>
                          )}
                          <Button size="sm" data-testid="admin-series-cancel-btn" onClick={() => seriesAction(sid, "declined")}
                            className="bg-red-500/10 border border-red-500/40 text-red-400 hover:bg-red-500/20">
                            <X className="w-4 h-4 mr-1.5" /> Cancella serie
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {bookings.length === 0 ? <Empty text="Nessun appuntamento richiesto per ora." /> :
              bookings.map((b) => <ItemCard key={b.booking_id} item={b} type="booking" onUpdate={updateIn(setBookings)} />)}
          </TabsContent>
          <TabsContent value="sessions" className="mt-6 space-y-4">
            {sessions.length === 0 ? <Empty text="Nessuna richiesta di sessione gratuita per ora." /> :
              sessions.map((s) => <ItemCard key={s.request_id} item={s} type="session" onUpdate={updateIn(setSessions)} />)}
          </TabsContent>
          <TabsContent value="messages" className="mt-6 space-y-4">
            {messages.length === 0 ? <Empty text="Nessun messaggio ricevuto." /> :
              messages.map((m) => (
                <div key={m.message_id} data-testid={`admin-message-${m.message_id}`}
                  className="bg-[#10141D] border border-[#222A3B] rounded-2xl p-5">
                  <p className="font-semibold text-white">{m.name} <span className="text-xs text-slate-500 font-normal">· {m.email}</span></p>
                  <p className="mt-2 text-sm text-slate-300">{m.message}</p>
                </div>
              ))}
          </TabsContent>
          <TabsContent value="safeguarding" className="mt-6 space-y-4">
            {reports.length === 0 ? <Empty text="Nessuna segnalazione ricevuta." /> :
              reports.map((r) => (
                <div key={r.report_id} data-testid={`admin-report-${r.report_id}`}
                  className="bg-[#10141D] border border-sky-500/25 rounded-2xl p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-white">{r.club}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {r.reporter_role === "soggetto_leso" ? "Segnalante: soggetto leso" : "Segnalante: soggetto terzo informato"}
                        {(r.first_name || r.last_name) && ` · Soggetto leso: ${[r.first_name, r.last_name].filter(Boolean).join(" ")}`}
                        {r.age ? ` · Età: ${r.age}` : ""}
                      </p>
                    </div>
                    <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border bg-sky-500/10 border-sky-500/40 text-sky-400">
                      Anonima
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-slate-300 whitespace-pre-wrap">{r.message}</p>
                </div>
              ))}
          </TabsContent>
          <TabsContent value="availability" className="mt-6">
            <AvailabilityTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
